// src/hooks/useFileDownload.ts
import { useCallback, useSyncExternalStore } from 'react';
import { generatePath } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { FileNode } from '../types/FileManagerTypes';
import { saveAs } from 'file-saver';
import axiosInstance from '../interceptor/axiosInstance';
import { ApiRoutes } from '../routes/routeConstants/apiRoutes';
import Notification, { destroyNotification } from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { PlacementPosition } from '../enums/placementEnums';
import { QueryKeys } from '../enums/queryKeys';
import { waitForJobEvent } from '../services/socket.service';

// Module-scope singleton -- deliberately outside the hook so the bulk-download
// job's polling and "in progress" state survive VFSBrowser unmounting (e.g.
// navigating away from File Manager entirely), not just route changes within it.
type BulkDownloadStatus = 'idle' | 'pending' | 'completed' | 'error';
let bulkDownloadState: { status: BulkDownloadStatus } = { status: 'idle' };
let pollTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

const BULK_DOWNLOAD_NOTIFICATION_KEY = 'bulk-download-progress';
// Backstop only — the `download:status` socket event normally ends the wait
// first. Covers a missed event or a reconnect gap.
const POLL_INTERVAL_MS = 30000;

const setBulkDownloadState = (next: { status: BulkDownloadStatus }) => {
  bulkDownloadState = next;
  listeners.forEach((listener) => listener());
};

const subscribeBulkDownload = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
};

const getBulkDownloadSnapshot = () => bulkDownloadState;

const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  // returnValue is deprecated but some browsers still need it set to show the prompt.
  e.returnValue = '';
};

// Tracks whether our own guard is currently attached, so we never call
// removeEventListener redundantly/out-of-order across the multiple exit
// paths in pollBulkDownloadJob below.
let beforeUnloadAttached = false;

const attachBeforeUnloadGuard = () => {
  if (beforeUnloadAttached) return;
  window.addEventListener('beforeunload', beforeUnloadHandler);
  beforeUnloadAttached = true;
};

const detachBeforeUnloadGuard = () => {
  if (!beforeUnloadAttached) return;
  window.removeEventListener('beforeunload', beforeUnloadHandler);
  beforeUnloadAttached = false;
};

/**
 * Kick off a browser download without navigating the current document.
 * Cross-origin S3 URLs ignore the `download` attribute; a top-level `<a>`
 * click can fire pagehide/beforeunload and tear down live preview sessions.
 * Hidden iframe + S3 `Content-Disposition: attachment` is the safe path.
 */
const triggerBrowserDownload = (url: string) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    iframe.remove();
  }, 60_000);
};

const isBulkDownloadPending = () => bulkDownloadState.status === 'pending';

const notifyDownloadInProgress = () => {
  Notification({
    message: 'A download is already in progress. Please wait for it to complete.',
    type: NotificationTypes.WARNING,
  });
};

async function pollBulkDownloadJob(jobId: string) {
  // "pending" is already claimed synchronously by handleBulkDownload before
  // this is called -- this function only polls and cleans up.
  attachBeforeUnloadGuard();
  Notification({
    message: 'Download in progress',
    description: 'Please wait — your files are being prepared. Do not close this tab.',
    type: NotificationTypes.INFO,
    placement: PlacementPosition.TOP,
    duration: 0,
    closable: false,
    key: BULK_DOWNLOAD_NOTIFICATION_KEY,
  });

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Corresponds to QueryKeys.POLL_BULK_DOWNLOAD_STATUS -- kept as a plain axios
      // call (not useQuery) since this loop is module-scope, outside any hook.
      const { data } = await axiosInstance.get(generatePath(ApiRoutes.FILEMANAGER_POLL_TO_TRIGGER_DOWNLOAD, { jobId }));

      if (data.status === 'ready') {
        // Detach the guard BEFORE triggering the download click. Otherwise the
        // synthetic <a>.click() (and any resulting browser-level download/
        // navigation prompt) races against our own beforeunload handler,
        // which can surface a confusing "leave site?" prompt right as the
        // file starts downloading.
        detachBeforeUnloadGuard();
        destroyNotification(BULK_DOWNLOAD_NOTIFICATION_KEY);

        if (data.url) {
          triggerBrowserDownload(data.url);
          Notification({ message: 'Download ready', type: NotificationTypes.SUCCESS });
        } else {
          // Previously this silently skipped the download but still reported
          // success -- surface it as an error instead so it isn't misleading.
          Notification({
            message: 'Download ready, but no file URL was returned. Please try again.',
            type: NotificationTypes.ERROR,
          });
        }
        return;
      }

      if (data.status === 'failed') {
        detachBeforeUnloadGuard();
        destroyNotification(BULK_DOWNLOAD_NOTIFICATION_KEY);
        Notification({ message: 'Download failed. Please try again.', type: NotificationTypes.ERROR });
        return;
      }

      // Wake on the socket event, or on the backstop timer if it never arrives.
      const waiter = waitForJobEvent('download:status', jobId);
      try {
        await Promise.race([
          new Promise((resolve) => {
            pollTimer = setTimeout(resolve, POLL_INTERVAL_MS);
          }),
          waiter.promise,
        ]);
      } finally {
        waiter.cancel();
        if (pollTimer) clearTimeout(pollTimer);
        pollTimer = null;
      }
    }
  } catch (error) {
    console.error('Bulk download polling error: ', error);
    detachBeforeUnloadGuard();
    destroyNotification(BULK_DOWNLOAD_NOTIFICATION_KEY);
    Notification({ message: 'Unable to prepare download. Please try again.', type: NotificationTypes.ERROR });
  } finally {
    // Safety net: no-op if already detached on one of the branches above,
    // but guarantees the guard never leaks past this function's lifetime.
    detachBeforeUnloadGuard();
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
    setBulkDownloadState({ status: 'idle' });
  }
}

export const useFileDownload = () => {
  const { status } = useSyncExternalStore(subscribeBulkDownload, getBulkDownloadSnapshot);

  const { mutateAsync: initiateBulkDownloadAsync } = useMutation({
    mutationKey: [QueryKeys.INITIATE_BULK_DOWNLOAD],
    mutationFn: (ids: string[]) => axiosInstance.post(ApiRoutes.FILEMANAGER_INITIATE_DOWNLOAD, { ids }).then((res) => res.data),
  });

  const handleGeoserverFileDownload = useCallback(async (fileId: string, fileType: string): Promise<any> => {
    try {
      const response = await axiosInstance.post('/filemanager/download-geoserver-files', { fileId, fileType }, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition ? contentDisposition.split('filename=')[1] : 'download.zip';
      const blob = new Blob([response.data]);
      saveAs(blob, filename);
      Notification({ message: 'File downloaded successfully.', type: NotificationTypes.SUCCESS });
    } catch (error) {
      Notification({ message: 'Failed to download file.', type: NotificationTypes.ERROR });
      console.error('Download error: ', error);
    }
  }, []);

  const handleBulkDownload = useCallback(
    async (selectedFiles: FileNode[]): Promise<void> => {
      if (isBulkDownloadPending()) return notifyDownloadInProgress();
      // Claim the slot synchronously, before the network round-trip, so a second
      // click can't race in while the initiate POST is still in flight.
      setBulkDownloadState({ status: 'pending' });
      try {
        const ids = selectedFiles.map((file) => file.id);
        const data = await initiateBulkDownloadAsync(ids);

        // A single file is never archived: the API presigns the object itself and
        // returns the link inline (no job queued), so download it straight away
        // and skip the poll loop entirely. Anything zipped answers with a jobId.
        if (data.url) {
          triggerBrowserDownload(data.url);
          Notification({ message: 'Download ready', type: NotificationTypes.SUCCESS });
          setBulkDownloadState({ status: 'idle' });
          return;
        }

        // Fire-and-forget: the poll loop is a module-scope function, not tied to
        // this component, so it keeps running even if the caller unmounts/navigates away.
        pollBulkDownloadJob(data.jobId);
      } catch (error) {
        console.error('Error initiating bulk download: ', error);
        Notification({ message: 'Unable to start download. Please try again.', type: NotificationTypes.ERROR });
        setBulkDownloadState({ status: 'idle' }); // release the slot -- polling never started
      }
    },
    [initiateBulkDownloadAsync],
  );

  return {
    handleGeoserverFileDownload,
    handleBulkDownload,
    isBulkDownloading: status === 'pending',
  };
};
